import React, { useCallback, useEffect, useState } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { OKRName } from 'components/RabbitOKRCreationPanel/PrimaryPanel/DetailsSection/OKRName';
import { ManualTracking } from 'components/RabbitOKRCreationPanel/PrimaryPanel/V2/sections/ManualTracking';
import { useOKRAccessControl } from 'components/RabbitOKRSidePanel/AccessControl/useOKRAccessControl';
// eslint-disable-next-line import/no-cycle
import { ChangeDetailsPanelHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import { resetLocalStateValues, updateLocalStateValues } from 'components/RabbitOKRSidePanel/KRslice';
import { CollaboratorsSection } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/CollaboratorsSection';
import { DescriptionSection } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/DescriptionSection';
import { ObjectiveLevelSection } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/ObjectiveLevelSection';
import { TagsSection } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/TagsSection';
import { TimePeriod } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/TimePeriod';
import { Tracking } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/Tracking';
import { VisibilitySection } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/VisibilitySection';
import { MIN_TITLE_LENGTH_TO_ENABLE_AI_BUTTON } from 'components/RabbitOKRSidePanel/constants';
import { QUERY_KEYS, useUpdateKRMutation } from 'components/RabbitOKRSidePanel/datahooks';
import {
  useAttachTagToOKRMutation,
  useModifyTagToOKRMutation,
  useOKRTagsQuery,
} from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import { useOKRRealignMutation } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignMutation';
import {
  closeChangeDetailsPanel,
  openTitleAndMetricPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { TimelineSelect } from 'components/TimelineSelect/TimelineSelect';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { checkGoalGenAIEnabled } from 'utils/helpers/featureFlags';

import { KeyResultIcon } from 'assets/assetsV1/iconComponents';
import { Objective as ObjectiveIcon } from 'assets/assetsV1/iconComponents/Objective';

import {
  OBJECTIVE_LEVEL,
  OBJECTIVE_TYPES_MAP,
  OKR_PROGRESS_TYPES,
  OKR_TYPES,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { roundOffToPlaces } from 'constants/helper';
import { sendAIButtonClickedEvent, sendGoalEditedClickedEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import { DrawerFooter } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import MavenButton from 'uiToolkitV1/MavenButton/MavenButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextLabel } from 'uiToolkitV1/Typography';

import { OKROwnerAndCollaboratorComponent } from '../Details/OKROwnerAndCollaborator';
import { ParentOKR } from '../Overview/ParentOKR';

type tagType = { uuid: string; tagName: string };
type tagKeyListType = tagType[];
type OwnersAndCollaborator = {
  id: number;
  ownerType: 'OWNER' | 'COLLABORATOR';
};

export const checkTagsModified = (existingTagsList: tagKeyListType, newTagList: tagKeyListType) => {
  if (!newTagList) return false;
  const existingTagsMap = {};
  const existingTags = existingTagsList || [];

  existingTags.forEach((tag: tagType) => {
    existingTagsMap[tag?.uuid] = tag;
  });
  const tagListMap = {};
  const tagKeyList = newTagList || [];
  tagKeyList.forEach((tag: tagType) => {
    tagListMap[tag?.uuid] = tag;
  });
  const newlyAttachedTags = tagKeyList.filter((tag) => !existingTagsMap[tag.uuid]);
  const deletedTags = existingTags.filter((tag: tagType) => !tagListMap[tag.uuid]);
  return newlyAttachedTags.length > 0 || deletedTags.length > 0;
};

export const checkOwnersAndCollaboratorsModified = (
  existingToIdsList: OwnersAndCollaborator[],
  newToIdsList: OwnersAndCollaborator[]
) => {
  if (!newToIdsList) return false;
  const existingToIdsMap = {};
  const existingToIds = existingToIdsList || [];

  existingToIds.forEach((employee: OwnersAndCollaborator) => {
    existingToIdsMap[employee?.id] = employee;
  });
  const toIdsListMap = {};
  const toIdsList = newToIdsList || [];
  toIdsList.forEach((employee: OwnersAndCollaborator) => {
    toIdsListMap[employee?.id] = employee;
  });
  const modifiedToIds = toIdsList.filter(
    (employee) => !existingToIdsMap[employee.id] || employee.ownerType !== existingToIdsMap?.[employee.id]?.ownerType
  );
  return modifiedToIds.length > 0 || existingToIds.length !== toIdsList.length;
};

export const checkDetailsModified = (localStateValues, okrDetails, customTags = []) => {
  const {
    text,
    description,
    timeBoundDetails,
    parentPost,
    measure,
    measureType,
    startingProgressValue,
    target,
    progressType,
    startTS,
    dueTS,
    visibilityType,
    objectiveLevel,
    departmentName,
  } = okrDetails || {};

  const isDetailsChanged =
    (localStateValues?.text !== undefined && localStateValues?.text !== text) ||
    (localStateValues?.description !== undefined && localStateValues?.description !== description) ||
    checkTagsModified(customTags, localStateValues?.tagKeyList) ||
    checkOwnersAndCollaboratorsModified(okrDetails?.toIds, localStateValues?.toIds) ||
    (localStateValues?.timeBoundDetails !== undefined &&
      localStateValues?.timeBoundDetails?.timelineTag !== timeBoundDetails?.timelineTag) ||
    (localStateValues?.parentPost !== undefined && localStateValues?.parentPost?.uuid !== (parentPost?.uuid ?? null)) ||
    (localStateValues?.measure !== undefined && localStateValues?.measure !== measure) ||
    (localStateValues?.measureType !== undefined && localStateValues?.measureType !== measureType) ||
    (localStateValues?.objectiveLevel !== undefined && localStateValues?.objectiveLevel !== objectiveLevel) ||
    (localStateValues?.departmentName !== undefined && localStateValues?.departmentName !== departmentName) ||
    (localStateValues?.startingProgressValue !== undefined &&
      +localStateValues.startingProgressValue !== startingProgressValue) ||
    (localStateValues?.target !== undefined && +localStateValues.target !== target) ||
    (localStateValues?.progressType !== undefined && localStateValues?.progressType !== progressType) ||
    (localStateValues?.startTS !== undefined && !moment(localStateValues?.startTS).isSame(startTS, 'date')) ||
    (localStateValues?.dueTS !== undefined && !moment(localStateValues?.dueTS).isSame(dueTS, 'date')) ||
    (localStateValues?.visibilityType !== undefined && localStateValues?.visibilityType !== visibilityType);

  return isDetailsChanged;
};

const ChangeDetailsPanel = () => {
  const dispatch = useDispatch();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const [isErrorInForm, setIsErrorInForm] = useState(false);

  const okrDetails = useSelector(kRDrawerSelector);
  const secondaryPanelDetailsObject = useSelector(kRDrawerSecondaryPanelSelector);
  const {
    type,
    localStateValues = {},
    progressType = OKR_PROGRESS_TYPES.MANUAL,
    parentProgressType,
    objectiveLevel,
    uuid,
    ifGoalChildExists,
    timeBoundDetails,
    parentPost,
  } = okrDetails || {};

  const { mutate: realign } = useOKRRealignMutation(uuid);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: customTags = [] } = useOKRTagsQuery({ okrId: uuid });
  const isDetailsChanged = checkDetailsModified(localStateValues, okrDetails, customTags);
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();

  const realignParentAndTimeline = useCallback(
    (payload) => {
      /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
      const {
        selectedParentUuid,
        timeBoundPeriod: localSelectedTimePeriod,
        performChildrenRealign: isChildrenRealignPanel,
      } = payload;
      const okrQueryKey = [QUERY_KEYS.KEY_RESULT, uuid];
      const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, uuid];
      try {
        realign(
          // @ts-ignore
          {
            selectedParentUuid,
            timeBoundPeriod: localSelectedTimePeriod,
            performChildrenRealign: isChildrenRealignPanel,
          },
          {
            onError: (err: { message?: string }) => {
              toast({
                title: err.message || 'Failed to realign',
                status: TOAST_STATUSES.ERROR,
                position: 'bottom-left',
              });
            },
            onSuccess: () => {
              queryClient.invalidateQueries(okrQueryKey);
              queryClient.invalidateQueries(alignmentQueryKey);

              queryClient.removeQueries(REACT_QUERY_KEYS.okrTreeView);

              if (isChildrenRealignPanel) {
                toast({
                  title: `Child ${modifyString(
                    moduleName,
                    StringModifier.PLURAL_SENTENCE_CASE
                  )} have been successfully re-aligned. You can now re-align your ${modifyString(
                    moduleName,
                    StringModifier.SENTENCE_CASE
                  )}.`,
                  status: TOAST_STATUSES.SUCCESS,
                  position: 'bottom-left',
                });
              } else {
                toast({
                  title: `The ${
                    type === OKR_TYPES.OBJECTIVE
                      ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                      : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                  } has been successfully re-aligned`,
                  status: TOAST_STATUSES.SUCCESS,
                  position: 'bottom-left',
                });
              }
              dispatch(closeChangeDetailsPanel({}));
            },
          }
        );
      } catch (err) {
        console.error(err.message);
      }
    },
    [dispatch, keyResultsName, moduleName, objectivesName, queryClient, realign, toast, type, uuid]
  );

  const { mutate: mutateOKR } = useUpdateKRMutation(uuid, () => {
    if (localStateValues?.realignApiPayload) {
      realignParentAndTimeline(localStateValues.realignApiPayload);
    } else {
      dispatch(closeChangeDetailsPanel({}));
    }
    queryClient.invalidateQueries(REACT_QUERY_KEYS.GoalsCascadedData as unknown as QueryKey);
  });

  const isObjective = okrDetails?.type === OKR_TYPES.OBJECTIVE;
  const { canEditOKR } = useOKRAccessControl();

  const { OBJECTIVE_TYPE } = useOKRConfig();
  const [pendingTagAPis, setPendingTagAPis] = useState(null);

  const { mutate: attachTagToOKR } = useAttachTagToOKRMutation(
    () => setPendingTagAPis((curPendingApis) => curPendingApis - 1),
    () => setPendingTagAPis((curPendingApis) => curPendingApis - 1)
  );
  const { mutate: modifyTagToOKR } = useModifyTagToOKRMutation(
    () => setPendingTagAPis((curPendingApis) => curPendingApis - 1),
    () => setPendingTagAPis((curPendingApis) => curPendingApis - 1)
  );

  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  const isCompactObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.COMPACT.value;

  const showTrackingSection = !isObjective || isDetailedObjectiveType;

  const showOwnersAndCollaborators = !isObjective || isCompactObjectiveType || isDetailedObjectiveType;
  const getAppropriateValue = (keyName: string) =>
    localStateValues?.[keyName] !== undefined ? localStateValues?.[keyName] : okrDetails[keyName];
  const currentParent = localStateValues?.parentPost ? { ...localStateValues?.parentPost } : parentPost;
  const isMeshMavenButtonDisabled =
    !currentParent?.parentPost?.uuid &&
    getAppropriateValue('text')?.replaceAll(' ', '').length < MIN_TITLE_LENGTH_TO_ENABLE_AI_BUTTON;

  const handleLocalSave = useCallback(
    (updates) => {
      dispatch(updateLocalStateValues(updates));
    },
    [dispatch]
  );

  useEffect(() => {
    if (localStateValues?.timeBoundDetails) {
      if (
        getAppropriateValue('startTS') < localStateValues?.timeBoundDetails?.startTS ||
        getAppropriateValue('startTS') > localStateValues?.timeBoundDetails?.endTS
      ) {
        dispatch(updateLocalStateValues({ startTS: localStateValues?.timeBoundDetails?.startTS }));
      }

      if (
        getAppropriateValue('dueTS') < localStateValues?.timeBoundDetails?.startTS ||
        getAppropriateValue('dueTS') > localStateValues?.timeBoundDetails?.endTS
      ) {
        dispatch(updateLocalStateValues({ dueTS: localStateValues?.timeBoundDetails?.endTS }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, localStateValues.dueTS, localStateValues.startTS, localStateValues?.timeBoundDetails]);

  useEffect(() => {
    if (pendingTagAPis === 0) {
      const newUpdates = { ...localStateValues };
      newUpdates.tagKeyList = undefined;
      const convertedValues = { startingProgressValue: undefined, target: undefined };
      if (localStateValues?.startingProgressValue !== undefined) {
        convertedValues.startingProgressValue = `${roundOffToPlaces(localStateValues.startingProgressValue, 2)}`;
      }
      if (localStateValues?.target !== undefined) {
        convertedValues.target = `${roundOffToPlaces(localStateValues.target, 2)}`;
      }
      // @ts-expect-error: expected params type
      mutateOKR({ updates: { ...newUpdates, ...convertedValues, localStateValues: undefined } });
      handleLocalSave({ ...localStateValues, ...convertedValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTagAPis]);

  const handleDiscardAndCancel = useCallback(() => {
    dispatch(resetLocalStateValues());
    dispatch(closeChangeDetailsPanel({}));
  }, [dispatch]);

  const handleBackOrCancelButtonClick = () => {
    if (isDetailsChanged) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          handleDiscardAndCancel();
        },
      });
    } else {
      handleDiscardAndCancel();
    }
  };

  const handleTagModification = useCallback(
    (tagKeyList: tagKeyListType) => {
      const existingTagsMap = {};
      const existingTags = customTags || [];

      existingTags.forEach((tag: tagType) => {
        existingTagsMap[tag?.uuid] = tag;
      });
      const tagListMap = {};
      tagKeyList.forEach((tag: tagType) => {
        tagListMap[tag?.uuid] = tag;
      });
      const newlyAttachedTags = tagKeyList.filter((tag) => !existingTagsMap[tag.uuid]);
      const deletedTags = existingTags.filter((tag: tagType) => !tagListMap[tag.uuid]);

      let curPendingApis = 0;
      newlyAttachedTags.forEach((tag) => {
        // @ts-expect-error: expected params type
        attachTagToOKR({ okrId: uuid, tagKeyId: tag?.uuid });
        curPendingApis += 1;
      });
      deletedTags.forEach((tag) => {
        // @ts-expect-error: expected params type
        modifyTagToOKR({ okrId: uuid, tagKeyId: tag?.uuid });
        curPendingApis += 1;
      });

      setPendingTagAPis(curPendingApis);
    },
    [attachTagToOKR, customTags, modifyTagToOKR, uuid]
  );

  const handleSave = useCallback(() => {
    const { tagKeyList } = localStateValues;
    if (tagKeyList) {
      handleTagModification(tagKeyList);
    } else {
      const convertedValues = { startingProgressValue: undefined, target: undefined };
      if (localStateValues?.startingProgressValue !== undefined) {
        convertedValues.startingProgressValue = `${roundOffToPlaces(localStateValues.startingProgressValue, 2)}`;
      }
      if (localStateValues?.target !== undefined) {
        convertedValues.target = `${roundOffToPlaces(localStateValues.target, 2)}`;
      }
      // @ts-expect-error: expected params type
      mutateOKR({ updates: { ...localStateValues, ...convertedValues, localStateValues: undefined } });
      handleLocalSave({ ...localStateValues, ...convertedValues });
    }
  }, [handleLocalSave, handleTagModification, localStateValues, mutateOKR]);

  const OkrIcon = isObjective ? ObjectiveIcon : KeyResultIcon;

  const { canCreateKeyResultAtRootLevel } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const isParentOptional =
    type === OKR_TYPES.OBJECTIVE ? objectiveLevel === OBJECTIVE_LEVEL.COMPANY : canCreateKeyResultAtRootLevel;

  const isParentMandatory = !isParentOptional;

  const labelText = (
    <span>
      What{' '}
      {isObjective
        ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
        : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)}{' '}
      this {modifyString(moduleName, StringModifier.SENTENCE_CASE)} align to.
      {isParentMandatory ? <sup style={{ top: 0, fontSize: '14px' }}>*</sup> : ''}
    </span>
  );

  return (
    // @ts-ignore
    <Flex backgroundColor="#FFFFFF" h="100%" flexDirection="column">
      <Box padding="8px" height="calc(100% - 72px)" display="flex" flexDirection="column" overflow="auto">
        <ChangeDetailsPanelHeader onCollapse={handleBackOrCancelButtonClick} />
        <Divider sx={{ borderColor: '#CFD7DF' }} marginBottom="12px" marginLeft="auto" marginRight="auto" width="95%" />
        <Flex flexDirection="column" padding="12px 16px" bg="#FDFDFD" border="1px solid #EBEFF3" borderRadius="8px">
          <Tooltip label="Title">
            <Box mb="16px">
              <OKRName
                isObjective={isObjective}
                text={getAppropriateValue('text')}
                // @ts-ignore
                onChange={(newText: string) => handleLocalSave({ text: newText })}
                showLabel={false}
                leftComponent={<OkrIcon boxSize="18px" color="#7651BC" />}
                isShowGenAI={false}
              />
            </Box>
          </Tooltip>
          {showTrackingSection && (localStateValues?.progressType ?? progressType) === OKR_PROGRESS_TYPES.MANUAL ? (
            <ManualTracking
              measure={getAppropriateValue('measure')}
              measureType={getAppropriateValue('measureType')}
              startingProgressValue={getAppropriateValue('startingProgressValue')}
              target={getAppropriateValue('target')}
              onValueChange={(update) => {
                handleLocalSave(update);
              }}
              parentProgressType={parentProgressType}
              isReadOnly={false}
            />
          ) : null}
          {/* <Alignment /> */}
          <Box w="100%" mb="16px">
            {/* @ts-ignore */}
            <Box display="flex" gap="16px">
              {/* @ts-ignore */}
              <Box display="flex" marginTop="auto" sx={{ '.chakra-menu__menu-button': { minWidth: '124px' } }}>
                <Tooltip label="Timeline" shouldWrapChildren>
                  <TimelineSelect
                    selectedTimeline={
                      localStateValues?.realignApiPayload
                        ? { ...timeBoundDetails, ...localStateValues?.realignApiPayload?.timeBoundPeriod }
                        : timeBoundDetails
                    }
                    showCalendarIcon
                    isReadOnly={false}
                    // @ts-ignore
                    onSelect={(newTimeBoundDetails) => {
                      handleLocalSave({
                        realignApiPayload: {
                          selectedParentUuid: undefined,
                          timeBoundPeriod: newTimeBoundDetails,
                          performChildrenRealign: false,
                        },
                        timeBoundDetails: newTimeBoundDetails,
                        // startTS: newTimeBoundDetails?.startTS,
                        // dueTS: newTimeBoundDetails?.dueTS,
                        parentPost: {
                          uuid: null,
                          text: 'None',
                          type: null,
                          visibilityType: null,
                        },
                      });
                    }}
                  />
                </Tooltip>
              </Box>
              <Box
                flex={1}
                overflow="hidden"
                sx={{
                  '.parent-okr-component': {
                    height: '44px',
                  },
                }}
              >
                <Tooltip label="Parent goal alignment" shouldWrapChildren>
                  <ParentOKR
                    okrType={type}
                    okrUuid={uuid}
                    okrText={getAppropriateValue('text')}
                    okrVisibilityType={getAppropriateValue('visibilityType')}
                    objectiveLevel={objectiveLevel}
                    timeBoundDetails={
                      localStateValues?.realignApiPayload
                        ? { ...timeBoundDetails, ...localStateValues?.realignApiPayload }
                        : timeBoundDetails
                    }
                    parentPost={localStateValues?.parentPost ? { ...localStateValues?.parentPost } : parentPost}
                    // @ts-ignore
                    labelText={labelText}
                    isReadOnly={!canEditOKR}
                    okrHasNoChildren={!ifGoalChildExists}
                    showLabel={false}
                    isOpenAsChild
                    leftPanelDetails={{ ...secondaryPanelDetailsObject }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>
          {showOwnersAndCollaborators && (
            <Flex
              width="100%"
              sx={{
                '.owners-collabs': {
                  width: '100%',
                },
                '.owners-collabs > div': {
                  height: '44px',
                },
              }}
              mb="16px"
            >
              <OKROwnerAndCollaboratorComponent
                isOwner
                ownersAndCollaborators={getAppropriateValue('toIds')}
                // @ts-ignore
                onChange={(update) => handleLocalSave({ toIds: update })}
                showName
                showIcon
              />
            </Flex>
          )}
          {checkGoalGenAIEnabled() && (
            <Flex justifyContent="flex-end" p="4px">
              <Tooltip
                label={`For AI assistance, set a parent ${modifyString(
                  moduleName,
                  StringModifier.TITLE_CASE
                )} or use a title with at least 10 characters.`}
                isDisabled={!isMeshMavenButtonDisabled}
              >
                <MavenButton
                  // @ts-ignore
                  text="Help me write"
                  onClick={() => {
                    sendAIButtonClickedEvent({
                      eventProperties: {
                        source: GOALS_EVENT_SOURCES.EDIT_GOAL_PANEL,
                        type: GOALS_EVENT_SOURCES.AI.type.HELP_ME_WRITE,
                        goalId: uuid || 'NA',
                        purpose: GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing,
                      },
                    });
                    dispatch(updateLocalStateValues({ suggestedOKRDetails: null }));
                    dispatch(
                      openTitleAndMetricPanel({
                        isNewOKR: false,
                        isOpenAsChild: true,
                        leftPanelDetails: { ...secondaryPanelDetailsObject },
                        isAlignmentDisabled: false,
                      })
                    );
                  }}
                  isDisabled={isMeshMavenButtonDisabled}
                />
              </Tooltip>
            </Flex>
          )}
        </Flex>

        <Box
          display="flex"
          padding="16px 12px 0px 12px"
          alignItems="center"
          gap="12px"
          alignSelf="stretch"
          mt="12px"
          mb="20px"
        >
          <TextLabel fontWeight="600">Other Details</TextLabel>
        </Box>
        <Box
          display="flex"
          padding="8px 12px"
          flexDirection="column"
          alignItems="flex-start"
          alignSelf="stretch"
          borderRadius="4px"
        >
          <Box width="100%">
            <TimePeriod isEditOKR />
          </Box>
          <Box width="100%">
            <VisibilitySection isEditOKR />
          </Box>
          <Box width="100%">
            <CollaboratorsSection isEditOKR />
          </Box>
          <Box width="100%">
            <ObjectiveLevelSection isEditOKR />
          </Box>
          <Box width="100%">{showTrackingSection && <Tracking isEditOKR />}</Box>
          <Box width="100%">
            <TagsSection isEditOKR />
          </Box>
          <Box width="100%">
            <DescriptionSection isEditOKR isLeftIcon setIsErrorInForm={setIsErrorInForm} />
          </Box>
        </Box>
      </Box>
      <DrawerFooter h="72px" backgroundColor="neutral.F5F7F9">
        <Button variant="tertiary" mr="auto" onClick={handleBackOrCancelButtonClick}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            sendGoalEditedClickedEvent({
              eventProperties: {
                source: 'Save Details Button',
                goalId: uuid || 'NA',
                type: GOALS_EVENT_SOURCES.EDIT_GOAL_PANEL,
              },
            });
            handleSave();
          }}
          isDisabled={!isDetailsChanged || isErrorInForm}
        >
          Save
        </Button>{' '}
      </DrawerFooter>
    </Flex>
  );
};

export { ChangeDetailsPanel };
